import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { ShoppingCart, Package, BarChart2, TrendingUp } from 'lucide-react';
import { getProducts } from '../api/Product/api';
import { getOrders } from '../api/Order/api';
import { getProjections } from '../api/Projection/api';
import { getSales } from '../api/Sale/api';

const StatCard = ({ title, value, subtitle, icon: Icon, color = '#14B8A6' }) => (
  <Paper 
    sx={{ 
      p: 3, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2,
      borderRadius: 2,
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
    }}
  >
    <Box sx={{ 
      p: 1.5, 
      borderRadius: 2, 
      bgcolor: `${color}15`, 
      color: color,
      display: 'flex'
    }}>
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h4" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
        {subtitle}
      </Typography>
    </Box>
  </Paper>
);

const Dashboard = () => {
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [orderPercentageChange, setOrderPercentageChange] = useState(0);
  const [projectionPercentage, setProjectionPercentage] = useState(0);
  const [serviceLevel, setServiceLevel] = useState(0);
  const [serviceLevelSubtitle, setServiceLevelSubtitle] = useState('Calculating...');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllSales = async (params) => {
    let allSales = [];
    let nextUrl = null;
    let pageCount = 0;
    do {
      pageCount++;
      const response = await getSales({ ...params, page: nextUrl ? new URL(nextUrl).searchParams.get('page') : 1 });
      allSales = [...allSales, ...(response?.data || [])];
      nextUrl = response?.next;
    } while (nextUrl);
    return allSales;
  };

  const fetchAllOrders = async (params) => {
    let allOrders = [];
    let nextUrl = null;
    let pageCount = 0;
    do {
      pageCount++;
      const response = await getOrders({ ...params, page: nextUrl ? new URL(nextUrl).searchParams.get('page') : 1 });
      allOrders = [...allOrders, ...(response?.data || [])];
      nextUrl = response?.next;
    } while (nextUrl);
    return allOrders;
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Get current, previous, and next month date ranges
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

      // Fetch products
      const productResponse = await getProducts({ page: 1, limit: 1 });
      const productTotal = productResponse?.total || productResponse?.data?.total || 0;
      setTotalProducts(productTotal);

      // Fetch current month orders
      const currentOrdersResponse = await getOrders({
        page: 1,
        limit: 1,
        startDate: currentMonthStart.toISOString(),
        endDate: currentMonthEnd.toISOString(),
      });
      const currentOrdersTotal = currentOrdersResponse?.total || 0;

      // Fetch previous month orders
      const previousOrdersResponse = await getOrders({
        page: 1,
        limit: 1,
        startDate: previousMonthStart.toISOString(),
        endDate: previousMonthEnd.toISOString(),
      });
      const previousOrdersTotal = previousOrdersResponse?.total || 0;

      // Calculate percentage change for orders
      let percentageChange = 0;
      if (previousOrdersTotal > 0) {
        percentageChange = ((currentOrdersTotal - previousOrdersTotal) / previousOrdersTotal) * 100;
      } else if (currentOrdersTotal > 0) {
        percentageChange = 100;
      }
      setTotalOrders(currentOrdersTotal);
      setOrderPercentageChange(percentageChange);

      // Fetch all orders for service level calculation
      const allOrders = await fetchAllOrders({
        startDate: previousMonthStart.toISOString(),
        endDate: previousMonthEnd.toISOString(),
        limit: 1000,
      });

      // Calculate service level
      let serviceLevel = 0;
      if (allOrders.length > 0) {
        // Fallback: Count non-cancelled orders (since no "COMPLETED" or "DELIVERED" status)
        // TODO: Update to use "DELIVERED" or similar when confirmed
        const fulfilledOrders = allOrders.filter(order => order.status !== 'CANCELLED');
        serviceLevel = (fulfilledOrders.length / allOrders.length) * 100;
      }
      setServiceLevel(serviceLevel);

      // Set subtitle based on target (90%)
      const targetServiceLevel = 90;
      const subtitle = serviceLevel === 0 && allOrders.length === 0
        ? 'No orders'
        : Math.abs(serviceLevel - targetServiceLevel) <= 0.5
          ? 'On target'
          : serviceLevel > targetServiceLevel
            ? 'Above target'
            : 'Below target';
      setServiceLevelSubtitle(subtitle);

      // Fetch projections
      const projectionResponse = await getProjections({ status: "DRAFT" });
      const projections = projectionResponse?.data;
      let projectionAccuracy = 0;
      if (projections && projections.length > 0) {
        // Filter projections for previous month
        const prevMonthProjections = projections.filter(proj => {
          const forecastDate = new Date(proj.forecast_date);
          return forecastDate >= previousMonthStart && forecastDate <= previousMonthEnd;
        });

        if (prevMonthProjections.length > 0) {
          // Fetch all sales
          const allSales = await fetchAllSales({ limit: 1000 });

          // Filter sales for previous month client-side
          const prevMonthSales = allSales.filter(sale => {
            const saleDate = new Date(sale.sales_date);
            return saleDate >= previousMonthStart && saleDate <= previousMonthEnd;
          });

          // Calculate accuracy by matching projections to sales
          const accuracies = prevMonthProjections.map(proj => {
            const matchingSales = prevMonthSales.filter(sale => 
              Number(sale.product) === Number(proj.product) && Number(sale.duka) === Number(proj.duka)
            );
            const totalSalesAmount = matchingSales.reduce((sum, sale) => sum + (sale.amount || 0), 0);
            const forecasted = proj.forecasted_quantity || 1; // Avoid division by zero
            return (totalSalesAmount / forecasted) * 100;
          });
          projectionAccuracy = accuracies.length > 0
            ? accuracies.reduce((sum, acc) => sum + acc, 0) / accuracies.length
            : 0;
        }
      }
      setProjectionPercentage(projectionAccuracy);

      setError(null);
    } catch (err) {
      setError("Failed to load dashboard data. Please try again later.");
      setTotalProducts(0);
      setTotalOrders(0);
      setOrderPercentageChange(0);
      setProjectionPercentage(0);
      setServiceLevel(0);
      setServiceLevelSubtitle('Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Dashboard
      </Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Orders"
            value={loading ? '...' : totalOrders.toLocaleString()}
            subtitle={
              loading
                ? 'Loading...'
                : `${orderPercentageChange >= 0 ? '+' : ''}${orderPercentageChange.toFixed(1)}% from last month`
            }
            icon={ShoppingCart}
            color="#14B8A6"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Active Products"
            value={loading ? '...' : totalProducts.toLocaleString()}
            subtitle={loading ? 'Loading...' : 'Across all dukas'}
            icon={Package}
            color="#6366F1"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Service Level"
            value={loading ? '...' : `${serviceLevel.toFixed(1)}%`}
            subtitle={serviceLevelSubtitle}
            icon={BarChart2}
            color="#22C55E"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Projections"
            value={loading ? '...' : `${projectionPercentage.toFixed(1)}%`}
            subtitle={loading ? 'Loading...' : 'Expected accuracy for next month'}
            icon={TrendingUp}
            color="#F59E0B"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
